import { LogInIcon } from "lucide-react"
import { lazy, Suspense } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router"

import { ErrorState } from "@/components/kit/states"
import { useAuthConfig } from "../api/auth"
import { AuthCard } from "../components/AuthCard"
import { FormSkeleton } from "../components/FormSkeleton"
import { signInProviders } from "../lib/providers"

/**
 * The form (react-hook-form, zod, the widgets) is its own chunk, fetched after
 * the first paint: preloading it with this screen made it compete with the
 * stylesheet and the font, and pushed the first paint past the LCP budget
 * (NFR-DSH-01). It needs `getConfig` anyway, so it loads while that does.
 */
const SignInForm = lazy(async () => ({
  default: (await import("../components/SignInForm")).SignInForm,
}))

const NOTICES = ["reused", "expired", "banned", "idle", "challenge"] as const
type NoticeKey = (typeof NOTICES)[number] | "reset"

/** Why the user is here, from the redirect that brought them (LLD §6.3). */
function noticeOf(params: URLSearchParams): NoticeKey | undefined {
  if (params.get("reset") === "1") return "reset"
  const reason = params.get("reason")
  return NOTICES.find((n) => n === reason)
}

/**
 * `/sign-in` (FR-DSH-PUB-01, LLD §6.8). The heading is painted before any
 * data: index.html carries this screen's first render, the form skeleton
 * included, as its static first paint (DW-61; `src/app/boot-screen.test.tsx`
 * holds the two equal). The form needs `getConfig` (registration, mode,
 * Turnstile and Google), so it shows once that answers.
 */
export default function SignInScreen() {
  const { t } = useTranslation("auth")
  const [params] = useSearchParams()
  const config = useAuthConfig()
  const notice = noticeOf(params)

  return (
    <AuthCard title={t("signIn.title")} icon={LogInIcon} description={t("signIn.subtitle")}>
      {config.isPending ? (
        <FormSkeleton />
      ) : config.isError ? (
        <ErrorState
          error={config.error}
          onRetry={() => {
            void config.refetch()
          }}
        />
      ) : (
        <Suspense fallback={<FormSkeleton />}>
          <SignInForm
            from={params.get("from")}
            notice={notice ? t(`notice.${notice}`) : undefined}
            providers={signInProviders(config.data.mode)}
            registrationOpen={config.data.mode === "normal" && config.data.registrationOpen}
          />
        </Suspense>
      )}
    </AuthCard>
  )
}
